import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import SectionHeader from "@quest_editor/components/SectionHeader";
import TablePagination from "@quest_editor/components/TablePagination";
import QuestStatusChip from "@quest_editor/components/Chips/QuestStatusChip";
import QuestCreateStatusChip from "@quest_editor/components/Chips/QuestCreateStatusChip";
import { Quest } from "@quest_editor/mocks";

const QuestTableSection = ({
  pageRows,
  totalCount,
  page,
  setPage,
  pageCount,
}: {
  pageRows: Quest[];
  totalCount: number;
  page: number;
  setPage: (p: number) => void;
  pageCount: number;
}) => {
  const { t } = useTranslation("questEditor");

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <SectionHeader title={t("promotion_detail.section.quests")} />

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("promotion_detail.table.header.id")}</TableCell>
              <TableCell>{t("promotion_detail.table.header.name")}</TableCell>
              <TableCell>
                {t("promotion_detail.table.header.status")}
              </TableCell>
              <TableCell>
                {t("promotion_detail.table.header.progress")}
              </TableCell>
              <TableCell align="right">
                <Box
                  px={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box />
                  <Stack direction="row" spacing={1}>
                    <SubActionButton size="small">
                      {t("promotion_detail.button.bulk_summary")}
                    </SubActionButton>
                    <SubActionButton size="small">
                      {t("promotion_detail.button.bulk_dl")}
                    </SubActionButton>
                  </Stack>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" py={4}>
                    {t("common.label.no_data")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((quest) => (
                <TableRow key={quest.id} hover>
                  <TableCell>{quest.id}</TableCell>
                  <TableCell>{quest.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <QuestStatusChip status={quest.status} />
                      {quest.status === "created" && (
                        <QuestCreateStatusChip status={quest.createStatus} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {quest.progress !== null
                      ? `${quest.progress} / ${quest.progressMax}`
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <SubActionButton
                        size="small"
                        component={Link}
                        to={`/quest_editor/quest/${quest.id}`}
                      >
                        {t("promotion_detail.button.detail")}
                      </SubActionButton>
                      <SubActionButton
                        size="small"
                        component={Link}
                        to={`/quest_editor/quest/${quest.id}/duplication`}
                        startIcon={<FileCopyIcon />}
                      >
                        {t("promotion_detail.button.duplication")}
                      </SubActionButton>
                      <SubActionButton size="small">
                        {t("promotion_detail.button.aggregate")}
                      </SubActionButton>
                      <SubActionButton size="small">
                        {t("promotion_detail.button.data_dl")}
                      </SubActionButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <TablePagination
        total={totalCount}
        page={page}
        setPage={setPage}
        count={pageCount}
      />
    </Paper>
  );
};

export default QuestTableSection;
